import React, { useState, useMemo, useRef } from 'react';
import html2canvas from 'html2canvas';
import globalResilienceData from '../../data/globalResilienceData.json';
import CountryFlag from '../common/countryFlag/country-flag';
import SelectCustom from '../common/react-select-custom/select-custom';
import RadarChart from '../common/radarChart/RadarChart';
import ResetButton from '../common/resetButton/ResetButton';
import IconDownload from '../common/icon/icon-download';
import IconRisk from '../common/icon/icon-risk';
import IconResilience from '../common/icon/icon-resilience';
import { formatRank } from '../../utils/utils';
import { resilienceConfig } from '../../config/project-config';
import styles from './CountryScorecard.module.scss';

const CountryScorecard = () => {
  const scorecardRef = useRef(null);
  // Get country code from URL parameter
  const urlParams = new URLSearchParams(window.location.search);
  const urlCountryCode = urlParams.get('countryCode');

  // State for selected country and override for URL
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [urlOverride, setUrlOverride] = useState(false);

  // Find country data based on URL parameter or selection
  const countryData = useMemo(() => {
    // If URL is overridden (reset clicked), ignore URL param
    if (urlOverride) {
      if (selectedCountry) {
        return globalResilienceData.find(country =>
          country.iso2?.toLowerCase() === selectedCountry.value?.toLowerCase()
        );
      }
      return null;
    }

    // Otherwise check URL first, then selection
    if (urlCountryCode) {
      return globalResilienceData.find(country =>
        country.iso2?.toLowerCase() === urlCountryCode.toLowerCase()
      );
    }
    if (selectedCountry) {
      return globalResilienceData.find(country =>
        country.iso2?.toLowerCase() === selectedCountry.value?.toLowerCase()
      );
    }
    return null;
  }, [urlCountryCode, selectedCountry, urlOverride]);

  // Prepare country options for select
  const countryOptions = useMemo(() => {
    return globalResilienceData.map(country => ({
      value: country.iso2,
      label: country.country
    })).sort((a, b) => a.label.localeCompare(b.label));
  }, []);

  // Calculate rank based on total score
  const countryRank = useMemo(() => {
    if (!countryData) return null;
    const sortedCountries = [...globalResilienceData].sort((a, b) =>
      (b.totalScore || 0) - (a.totalScore || 0)
    );
    return sortedCountries.findIndex(country =>
      country.iso2 === countryData.iso2
    ) + 1;
  }, [countryData]);

  const handleCountryChange = (option) => {
    setSelectedCountry(option);
  };

  const resetSelection = () => {
    setSelectedCountry(null);
    setUrlOverride(true); // Override URL parameter
  };

  const handleDownloadScorecard = async () => {
    if (!scorecardRef.current || !countryData) return;

    try {
      // Hide buttons temporarily during capture
      const buttons = scorecardRef.current.querySelector(`.${styles.headerActions}`);
      if (buttons) buttons.style.display = 'none';

      const canvas = await html2canvas(scorecardRef.current, {
        backgroundColor: '#ffffff',
        scale: 2, // Higher quality
        useCORS: true,
        logging: false,
      });

      // Show buttons again
      if (buttons) buttons.style.display = 'flex';

      // Convert to blob and download
      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${countryData.country.replace(/\s+/g, '-')}-Scorecard.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      });
    } catch (error) {
      console.error('Error generating scorecard image:', error);
    }
  };

  const getRiskIndicators = () => {
    return resilienceConfig.riskMetrics.map(metric => ({
      key: metric.key,
      label: metric.label,
      value: countryData[metric.key]
    }));
  };

  const renderRiskIndicators = () => {
    const riskIndicators = getRiskIndicators();
    return riskIndicators.map(indicator => (
      <div key={indicator.key} className={styles.indicatorItem}>
        <span className={styles.indicatorLabel}>{indicator.label}:</span>
        <span className={`${styles.indicatorValue} ${styles.riskValue}`}>{indicator.value?.toFixed(2) || 0}</span>
      </div>
    ));
  };

  const getResilienceIndicators = () => {
    return resilienceConfig.resilienceMetrics.map(metric => ({
      key: metric.key,
      label: metric.label,
      value: countryData[metric.key]
    }));
  };

  const renderResilienceIndicators = () => {
    const resilienceIndicators = getResilienceIndicators();
    return resilienceIndicators.map(indicator => (
      <div key={indicator.key} className={styles.indicatorItem}>
        <span className={styles.indicatorLabel}>{indicator.label}:</span>
        <span className={`${styles.indicatorValue} ${styles.resilienceValue}`}>{indicator.value?.toFixed(2) || 0}</span>
      </div>
    ));
  };

  return (
    <>
      {/* Country Selection (shown when no country is selected) */}
      {!countryData && (
        <div className={styles.countrySelection}>
            <div className="row">
                <div className="col-12 col-md-3">
                    <div className={styles.selectContainer}>
                        <label className={styles.selectLabel}>Select Country/Territory:</label>
                        <SelectCustom
                            value={selectedCountry}
                            options={countryOptions}
                            onChange={handleCountryChange}
                            placeholder="Type here"
                            isSearchable={true}
                            isClearable={true}
                            selectType="passport"
                            darkTheme={true}
                        />
                    </div>
                </div>
            </div>

        </div>
      )}

      {/* Country Details */}
      {countryData && (
        <div ref={scorecardRef} className={styles.countryWrapper}>
          {/* Header Information */}
          <div className={styles.countryHeader}>
              {/* Mobile reset button */}
              <ResetButton onClick={resetSelection} className={styles.resetButtonMobile} />
            <div className={`${styles.countryTitleRow} row flex-column flex-md-row align-items-md-center justify-content-between `}>
              <div className={`${styles.countryTitle} col-auto`}>
                <CountryFlag countryName={countryData.country} code={countryData.iso2} size="large" />
              </div>

              <div className={`${styles.headerActions} col-auto`}>
                {/* Desktop reset button */}
                <ResetButton onClick={resetSelection} className={styles.resetButtonDesktop} />
                <button onClick={handleDownloadScorecard} className={styles.downloadButton}>
                  Download Scorecard
                  <IconDownload />
                </button>
              </div>
            </div>

            <div className={`${styles.countryStats} row flex-column flex-md-row align-items-md-end justify-content-between`}>
                <div className="col">
                    <div className="row flex-column flex-md-row align-items-md-end ">
                        <div className={`${styles.statGroup} col-auto`}>
                            <div className={styles.statLabel}>Rank</div>
                            <div className={`${styles.statValue} ${styles.statLeft}`}>{formatRank(countryRank)}</div>
                        </div>

                        <div className={`${styles.statGroup} col-auto`}>
                            <div className={styles.statLabel}>Total Global Investment Risk and<br /> Resilience Index Score (Out of 100)</div>
                            <div className={`${styles.statValue} ${styles.statLeft}`}>{countryData.totalScore?.toFixed(2) || 0}</div>
                        </div>

                        <div className={`${styles.statGroup} col-auto`}>
                            <div className={styles.statLabel}>Investment Category</div>
                            <div className={`${styles.statValue} ${styles.statLeft}`}>{countryData.scoreCategory}</div>
                        </div>
                    </div>

                </div>
                <div className="col-md-5">
                    <div className="row flex-column flex-md-row align-items-md-end text-md-right justify-content-md-end">
                        <div className={`${styles.statGroup} col-auto`}>
                            <div className={styles.statLabel}>Region</div>
                            <div className={`${styles.statValue} ${styles.statRight}`}>{countryData.region}</div>
                        </div>

                        <div className={`${styles.statGroup} col-auto`}>
                            <div className={styles.statLabel}>Income Group<br /> (Gross National Income)</div>
                            <div className={`${styles.statValue} ${styles.statRight}`}>{countryData.incomeGroup}</div>
                        </div>

                        <div className={`${styles.statGroup} col-auto`}>
                            <div className={styles.statLabel}>GDP Growth<br /> (5-Year Average)</div>
                            <div className={`${styles.statValue} ${styles.statRight}`}>{countryData.gdpGrowth}</div>
                        </div>
                    </div>
                </div>
            </div>
          </div>

          {/* Main Content */}
          <div className={styles.mainContent}>
            {/* Risk Section */}
            <div className={styles.section}>


              <div className={styles.sectionContent}>
                <div className="row">
                  <div className="col-md-6">
                      <div className={styles.sectionHeader}>
                          <div className={styles.sectionTitle}>
                              <IconRisk />
                              <h3>Risk</h3>
                          </div>
                      </div>
                    <div className={styles.scoreInfo}>
                      <div className={styles.scoreGroup}>
                        <div className={styles.scoreLabel}>
                            Total Risk Score
                            <br /><span>(Out of 100, Lower is Better)</span>
                        </div>
                        <div className={`${styles.scoreValue} ${styles.riskValue} text-right`}>{countryData.riskScore?.toFixed(1) || 0}</div>
                      </div>
                      <div className={styles.scoreGroup}>
                        <div className={styles.categoryLabel}>Risk Score Category</div>
                        <div className={`${styles.categoryValue} ${styles.riskValue} text-right`}>{countryData.riskCategory}</div>
                      </div>
                    </div>

                    <div className={styles.indicators}>
                        <div className={styles.introTitle}>Risk Indicators<br /><span>(0‒1 Scale, Lower is Better)</span></div>
                      {renderRiskIndicators()}
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className={styles.radarContainer}>
                      <RadarChart
                        data={getRiskIndicators()}
                        color={resilienceConfig.colors.risk}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Resilience Section */}
            <div className={styles.section}>
              <div className={styles.sectionContent}>
                <div className="row">
                  <div className="col-md-6">
                      <div className={styles.sectionHeader}>
                          <div className={styles.sectionTitle}>
                              <IconResilience />
                              <h3>Resilience</h3>
                          </div>
                      </div>
                    <div className={styles.scoreInfo}>
                      <div className={styles.scoreGroup}>
                        <div className={styles.scoreLabel}>
                            Total Resilience Score<br />
                            <span>(Out of 100, Higher is Better)</span>
                        </div>
                        <div className={`${styles.scoreValue} ${styles.resilienceValue} text-right`}>{countryData.resilienceScore?.toFixed(2) || 0}</div>
                      </div>
                      <div className={styles.scoreGroup}>
                        <div className={styles.categoryLabel}>Resilience Score Category</div>
                        <div className={`${styles.categoryValue} ${styles.resilienceValue} text-right`}>{countryData.resilienceCategory}</div>
                      </div>
                    </div>

                    <div className={styles.indicators}>
                        <div className={styles.introTitle}>Resilience Indicators<br /><span>(0‒1 Scale, Higher is Better)</span></div>
                      {renderResilienceIndicators()}
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className={styles.radarContainer}>
                      <RadarChart
                        data={getResilienceIndicators()}
                        color={resilienceConfig.colors.resilience}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Country Not Found */}
      {!countryData && urlCountryCode && !urlOverride && (
        <div className={styles.notFound}>
          <p>Country with code "{urlCountryCode}" not found.</p>
        </div>
      )}
    </>
  );
};

export default CountryScorecard;